import parse, { domToReact } from "html-react-parser";
import { createElement, Fragment } from "react";
import parseStyle from "style-to-object";

import { ScrollShadowWrapper } from "../components/Default/LekUI/ScrollShadowWrapper";
import { colorsToOverwrite } from "./colors";

export const isHtml = (text) => {
  const htmlRegex = /<\/?[a-z][\s\S]*>/i;
  return htmlRegex.test(text);
};

const options = {
  replace: (domNode) => {
    try {
      if (domNode.attribs?.contenteditable) {
        delete domNode.attribs.contenteditable;
      }

      if (domNode.name === "p") {
        return parseParagraph(domNode);
      }

      if (domNode.name === "a") {
        return parseLink(domNode);
      }

      if (domNode.type === "text" && domNode.data.includes("\n")) {
        return parseEmptyLine(domNode);
      }

      if (domNode.attribs?.style) {
        if (
          !["th", "tr", "table", "td", "blackquote", "hr", "img"].includes(
            domNode.name,
          )
        ) {
          const styleObject = parseStyle(domNode.attribs.style);
          return parseColors(domNode, styleObject);
        }
      }
    } catch {
      return domNode;
    }

    return undefined;
  },
  transform: (reactNode, domNode, index) => {
    if (domNode.name === "table") {
      return transformTable(reactNode, index);
    }

    return reactNode;
  },
};

const transformTable = (reactNode, index) => {
  return <ScrollShadowWrapper key={`table-${index}`}>{reactNode}</ScrollShadowWrapper>;
};

const isBlankText = (text) => {
  if (!text) return true;
  return text.replace(/ /g, " ").trim() === "";
};

const INLINE_WRAPPERS = new Set([
  "span",
  "em",
  "strong",
  "b",
  "i",
  "u",
  "font",
  "small",
  "mark",
]);

const isEmptyNode = (node) => {
  if (!node) return true;
  if (node.type === "text") return isBlankText(node.data);
  if (node.name === "br") return true;
  if (INLINE_WRAPPERS.has(node.name)) {
    const children = node.children || [];
    return children.every(isEmptyNode);
  }
  return false;
};

const isEmptyParagraph = (domNode) => {
  const children = domNode.children || [];
  return children.every(isEmptyNode);
};

const parseParagraph = (domNode) => {
  if (isEmptyParagraph(domNode)) {
    return createElement(Fragment);
  }

  const { class: cls, style: _style, ...restAttribs } = domNode.attribs || {};
  const newAttribs = { ...restAttribs };
  if (cls) {
    newAttribs.className = cls;
  }
  newAttribs.style = { margin: 0 };

  return createElement(
    "p",
    newAttribs,
    domNode.children ? domToReact(domNode.children, options) : null,
  );
};

const parseEmptyLine = (domNode) => {
  return (
    <>
      {domNode.data.split("\n").map((line, index) => (
        <Fragment key={index}>
          {line}
          {index < domNode.data.split("\n").length - 1 && (
            <div className="py-2" />
          )}
        </Fragment>
      ))}
    </>
  );
};

// ponytail: React wywala się na void elemencie z children (nawet pustą tablicą)
const VOID_ELEMENTS = new Set([
  "area",
  "base",
  "br",
  "col",
  "embed",
  "hr",
  "img",
  "input",
  "link",
  "meta",
  "source",
  "track",
  "wbr",
]);

const parseColors = (domNode, styleObject) => {
  if (VOID_ELEMENTS.has(domNode.name)) {
    return domNode;
  }

  const { class: cls, ...restAttribs } = domNode.attribs;
  const newAttribs = { ...restAttribs };
  if (cls) {
    newAttribs.className = cls;
  }
  newAttribs.style = {
    color: colorsToOverwrite[styleObject.color] || styleObject.color,
  };

  try {
    return createElement(
      domNode.name,
      newAttribs,
      domNode.children ? domToReact(domNode.children, options) : null,
    );
  } catch {
    return domNode;
  }
};

const parseLink = (domNode) => {
  const { style: _style, class: cls, ...attribs } = domNode.attribs;
  const newAttribs = { ...attribs };
  newAttribs.className = `${
    (newAttribs.className ? `${newAttribs.className} ` : "") +
    (cls ? `${cls} ` : "")
  }link`;
  if (newAttribs.href) {
    newAttribs.target = "_blank";
    newAttribs.rel = "noopenner norefferer";
  }

  try {
    return createElement(
      domNode.name,
      newAttribs,
      domNode.children ? domToReact(domNode.children, options) : null,
    );
  } catch {
    return domNode;
  }
};

export const htmlFormatter = (html) => {
  if (!html) {
    return "";
  }

  try {
    return parse(html, options);
  } catch {
    return parse(html);
  }
};
